import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Send, CheckCircle2, XCircle, BookCheck, RotateCcw, Ban, Plus, FileText, History, Paperclip, ShieldCheck, Search, X, Coins, ExternalLink, ChevronRight, Banknote, AlertTriangle,
} from 'lucide-react';

import { arReceiptApi }  from '@/api/arReceipt.api';
import { chequeApi }     from '@/api/cheque.api';
import { bankApi, bankBranchApi } from '@/api/bank.api';
import { customerApi }   from '@/api/customer.api';
import { accountApi }    from '@/api/account.api';
import { taxApi }        from '@/api/tax.api';
import { withheldOnNet } from '@/utils/withholding';
import type { WithholdingTaxRule } from '@/api/tax.api';
import type { BankListDto, BankBranchListDto } from '@/types/bank.types';
import { FundingSourceSelect } from '@/components/finance/FundingSourceSelect';
import { currencyApi }   from '@/api/currency.api';
import { exchangeRateApi } from '@/api/exchangeRate.api';
import { MasterReportPreview } from '@/components/master/report/MasterReportPreview';
import { ArReceiptVoucher }    from '@/components/ar/ArReceiptVoucher';
import { exportToExcel }       from '@/utils/reportExport';
import { usePermission } from '@/hooks/usePermission';
import { Permissions }   from '@/constants/permissions';
import { AppRoutes }     from '@/constants/routes';
import { Spinner }       from '@/components/ui/Spinner';
import { Button }        from '@/components/ui/Button';
import { IconButton }    from '@/components/ui/IconButton';
import { Input }         from '@/components/ui/Input';
import { Select }        from '@/components/ui/Select';
import { Field }         from '@/components/ui/Field';
import { Tabs }          from '@/components/ui/Tabs';
import { ReasonDialog }  from '@/components/ui/ReasonDialog';
import { PageHeader }    from '@/components/layout/PageHeader';
import { MasterToolbar } from '@/components/master/MasterToolbar';
import { MasterFlash }   from '@/components/master/MasterFlash';
import { LinesGrid, type LineColumn } from '@/components/grids/LinesGrid';
import { MasterSearchModal } from '@/components/master/MasterSearchModal';
import { ERPContextBar }    from '@/components/erp/ERPContextBar';
import { WorkflowStatusPanel } from '@/components/erp/WorkflowStatusPanel';
import { AttachmentPanel }   from '@/components/erp/AttachmentPanel';
import { AuditViewer }       from '@/components/erp/AuditViewer';
import { setErpContext, clearErpContext } from '@/state/erpContextSlice';
import { cn }            from '@/utils/cn';

import type { RootState }          from '@/state/store';
import { deriveVoucherPaymentMethod } from '@/utils/voucherPaymentMethod';
import { costCentresForLocation } from '@/utils/costCentreScope';
import type { SearchColumn }       from '@/components/master/MasterSearchModal';
import type { CustomerListDto }    from '@/types/customer.types';
import type {
  ArReceiptDto,
  ArReceiptLineRequest,
  ArReceiptAllocationRequest,
  CreateArReceiptRequest,
  UpdateArReceiptRequest,
  PaymentMethod,
} from '@/types/arReceipt.types';
import { VoucherAllocationPicker } from '@/components/fin/VoucherAllocationPicker';
import type {
  VoucherAllocationPick, VoucherAllocationSummary, SavedVoucherAllocation,
} from '@/components/fin/VoucherAllocationPicker';
import { PAYMENT_METHOD_LABELS } from '@/types/arReceipt.types';
import { clearedByMethodChange } from '@/utils/paymentLineFields';

// ── Search columns ────────────────────────────────────────────────────────────

const CUSTOMER_COLUMNS: SearchColumn<CustomerListDto>[] = [
  { key: 'code', label: 'Code', className: 'w-28 font-mono text-blue-700 dark:text-blue-400' },
  { key: 'name', label: 'Customer Name' },
  { key: 'currencyCode', label: 'CCY', className: 'w-16 font-mono text-slate-500 dark:text-gray-400' },
];

// ── Line row ──────────────────────────────────────────────────────────────────

interface LineRow {
  key:           string;
  lineNumber:    number;
  paymentType:   PaymentMethod;
  bankAccountId: string;
  /** Cash GL account this line lands in. Empty for bank methods, which resolve via the bank. */
  fundingGlAccountId: string;
  chequeNumber:  string;
  chequeDate:    string;
  /** The customer's bank and branch this cheque is drawn on (GAP-014) — not bankAccountId (ours). */
  drawerBankId:   string;
  drawerBranchId: string;
  cardReference: string;
  amount:        number;
  currencyCode:  string;
  exchangeRate:  number;
  remarks:       string;
}

const mkLine = (n: number, ccy: string, xr: number): LineRow => ({
  key:           `new-${Date.now()}-${n}-${Math.random()}`,
  lineNumber:    n,
  paymentType:   'Cash',
  bankAccountId: '',
  fundingGlAccountId: '',
  chequeNumber:  '',
  chequeDate:    '',
  drawerBankId:   '',
  drawerBranchId: '',
  cardReference: '',
  amount:        0,
  currencyCode:  ccy,
  exchangeRate:  xr,
  remarks:       '',
});

const PAYMENT_METHODS: PaymentMethod[] = [
  'Cash', 'Cheque', 'BankTransfer', 'CreditCard', 'DebitCard', 'OnlineBanking', 'Other',
];

// ── Tabs ──────────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'lines',       label: 'Payment Lines',     icon: FileText    },
  { key: 'settles',     label: 'Invoices Settled',  icon: Coins       },
  { key: 'workflow',    label: 'Workflow',       icon: History     },
  { key: 'attachments', label: 'Attachments',    icon: Paperclip   },
  { key: 'audit',       label: 'Audit Log',      icon: ShieldCheck },
] as const;
type Tab = (typeof TABS)[number]['key'];

// ── Workflow status colours (reusing AR Invoice palette) ──────────────────────

const STATUS_COLORS: Record<string, string> = {
  Draft:          'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-gray-400',
  Submitted:      'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  PendingApproval:'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
  Approved:       'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  Rejected:       'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
  Posted:         'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400',
  Reversed:       'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
};

const fmt = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── Main Component ────────────────────────────────────────────────────────────

export const ArReceiptFormPage = () => {
  const { id }   = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isNew    = !id;

  const { hasPermission } = usePermission();
  const canEdit    = hasPermission(Permissions.AR_RECEIPT_EDIT);
  const canRaiseOnAccount = hasPermission(Permissions.AR_RECEIPT_ON_ACCOUNT);
  const canSubmit  = hasPermission(Permissions.AR_RECEIPT_SUBMIT);
  const canApprove = hasPermission(Permissions.AR_RECEIPT_APPROVE);
  const canReject  = hasPermission(Permissions.AR_RECEIPT_REJECT);
  const canPost    = hasPermission(Permissions.AR_RECEIPT_POST);
  const canReverse = hasPermission(Permissions.AR_RECEIPT_REVERSE);
  const canCancel  = hasPermission(Permissions.AR_RECEIPT_CANCEL);
  const canDelete  = hasPermission(Permissions.AR_RECEIPT_DELETE);
  const canPrint   = hasPermission(Permissions.AR_RECEIPT_PRINT);
  const canAddDoc  = hasPermission(Permissions.DOC_ATTACHMENT_ADD);
  const canDelDoc  = hasPermission(Permissions.DOC_ATTACHMENT_DELETE);

  const authUser   = useSelector((s: RootState) => s.auth.user);
  const session    = useSelector((s: RootState) => s.session.session);
  const accessibleLocations   = useSelector((s: RootState) => s.auth.accessibleLocations);
  const accessibleCostCenters = useSelector((s: RootState) => s.auth.accessibleCostCenters);

  // ── Reference data ────────────────────────────────────────────────────────
  const [currencies, setCurrencies] = useState<{ code: string; name: string }[]>([]);

  useEffect(() => {
    currencyApi.getList({ pageSize: 100, isActive: true })
      .then(r => setCurrencies((r.data.data?.items ?? []).map((c: { code: string; name: string }) => ({ code: c.code, name: c.name }))))
      .catch(() => {});
  }, []);

  // ── Header state ──────────────────────────────────────────────────────────
  const today = new Date().toISOString().split('T')[0];
  const baseCurrency = authUser?.baseCurrencyCode ?? session?.companyCurrency ?? 'USD';

  const [customerId,    setCustomerId]    = useState('');
  const [customerCode,  setCustomerCode]  = useState('');
  const [customerName,  setCustomerName]  = useState('');
  const [controlAccountId, setControlAccountId] = useState<string | null>(null);
  const [receiptDate,   setReceiptDate]   = useState(today);
  const [postingDate,   setPostingDate]   = useState(today);
  const [currencyCode,  setCurrencyCode]  = useState(baseCurrency);
  const [exchangeRate,  setExchangeRate]  = useState(1);
  const [reference,     setReference]     = useState('');
  const [narration,     setNarration]     = useState('');
  const [locationId,    setLocationId]    = useState('');
  const [costCenterId,  setCostCenterId]  = useState('');

  // The customer's drawer bank/branch on a received cheque line (GAP-014) — loaded once, filtered
  // per row by the line's chosen bank, same master list every Cheque line picks from.
  const [drawerBanks,    setDrawerBanks]    = useState<BankListDto[]>([]);

  /**
   * Lines whose detail is open. The drawer's bank and branch belong to a cheque somebody handed
   * us and to nothing else, so as columns they were two disabled cells showing "—" on every cash,
   * card and transfer line, and 256px of width the rest of the grid could use.
   */
  const [openDetail, setOpenDetail] = useState<Set<string>>(new Set());

  const toggleDetail = (key: string) =>
    setOpenDetail(open => {
      const next = new Set(open);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  const [drawerBranches, setDrawerBranches] = useState<BankBranchListDto[]>([]);
  useEffect(() => {
    bankApi.getList({ pageNumber: 1, pageSize: 500 })
      .then(r => setDrawerBanks(r.data.data?.items ?? []))
      .catch(() => setDrawerBanks([]));
    bankBranchApi.getList({ pageNumber: 1, pageSize: 500 })
      .then(r => setDrawerBranches(r.data.data?.items ?? []))
      .catch(() => setDrawerBranches([]));
  }, []);

  const [custOpen, setCustOpen]       = useState(false);

  const fetchCustomers = async (search: string, page: number, pageSize: number) => {
    const res = await customerApi.getList({ search, pageNumber: page, pageSize, isActive: true });
    const d = res.data.data;
    return { items: d?.items ?? [], totalCount: d?.totalCount ?? 0 };
  };

  // Seed location/CC from session on new
  useEffect(() => {
    if (!isNew) return;
    setLocationId(session?.locationId ?? '');
    setCostCenterId(session?.costCenterId ?? '');
  }, [isNew, session?.locationId, session?.costCenterId]);

  // Auto-fetch exchange rate
  useEffect(() => {
    if (!currencyCode || currencyCode === baseCurrency) {
      if (isNew) setExchangeRate(1);
      return;
    }
    exchangeRateApi.getList({ fromCurrencyCode: baseCurrency, toCurrencyCode: currencyCode, pageSize: 10 })
      .then(res => {
        const items = res.data?.data?.items ?? [];
        const sorted = items.filter((r: { effectiveDate: string }) => r.effectiveDate <= receiptDate)
          .sort((a: { effectiveDate: string }, b: { effectiveDate: string }) => b.effectiveDate.localeCompare(a.effectiveDate));
        const rate = sorted.length > 0 ? sorted[0].rate : items.length > 0 ? items[0].rate : null;
        if (rate == null) return;
        setExchangeRate(rate);
        // Lines the user just moved to this currency take its rate too (see changeCurrency).
        if (syncLineRates.current) {
          syncLineRates.current = false;
          setLines(ls => ls.map(l => l.currencyCode === currencyCode ? { ...l, exchangeRate: rate } : l));
        }
      }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencyCode, receiptDate]);

  // ── Which methods this company actually permits ───────────────────────────
  //
  // Same reason as the payment form: cfg_payment_method says which methods are allowed in which
  // direction, the save validator enforces it, and offering a method the server will refuse leaves
  // the operator with an empty "Receive into" and no explanation. Falls back to the full list if
  // the call fails — a lookup that did not answer is not a policy.
  const [allowedMethods, setAllowedMethods] = useState<PaymentMethod[]>(PAYMENT_METHODS);

  useEffect(() => {
    accountApi.allowedPaymentMethods(false)
      .then(res => {
        const permitted = res.data.data ?? [];
        const offered = PAYMENT_METHODS.filter(m => permitted.includes(m));
        if (offered.length > 0) setAllowedMethods(offered);
      })
      .catch(() => {});
  }, []);

  // ── Lines ─────────────────────────────────────────────────────────────────
  const [lines, setLines] = useState<LineRow[]>([mkLine(1, baseCurrency, 1)]);

  // Changing the voucher's currency carries the lines on the old one with it, so "Deposit to" offers
  // accounts in the currency actually being received — same fix as the AP payment form (QC P7-102).
  // Only on a user's change, never on load; a line set to another currency by hand is left alone.
  const syncLineRates = useRef(false);
  const changeCurrency = (next: string) => {
    setLines(ls => ls.map(l => l.currencyCode === currencyCode
      ? { ...l, currencyCode: next, exchangeRate: next === baseCurrency ? 1 : l.exchangeRate }
      : l));
    syncLineRates.current = next !== baseCurrency;
    setCurrencyCode(next);
  };

  const updateLine = (key: string, patch: Partial<LineRow>) =>
    setLines(ls => ls.map(l => l.key === key ? { ...l, ...patch } : l));

  const addLine = () =>
    setLines(ls => [...ls, mkLine(ls.length + 1, currencyCode, exchangeRate)]);

  const removeLine = (key: string) =>
    setLines(ls => ls.filter(l => l.key !== key));

  // Withholding: the rule picked here, the rules a customer receipt may use, and the rule a saved
  // voucher already carries (shown even if it has since been deactivated, so it does not silently
  // read as "None").
  const [withholdingRuleId,    setWithholdingRuleId]    = useState('');
  const [savedWithholdingCode, setSavedWithholdingCode] = useState<string | null>(null);
  const [withholdingRules,     setWithholdingRules]     = useState<WithholdingTaxRule[]>([]);

  useEffect(() => {
    taxApi.getWithholdingRules({ isActive: true, pageSize: 200 })
      .then(r => setWithholdingRules(
        (r.data?.data?.items ?? []).filter(x => x.appliesTo === 'Customer' || x.appliesTo === 'Both')))
      .catch(() => setWithholdingRules([]));
  }, []);

  const totalAmount = lines.reduce((s, l) => s + (l.amount || 0), 0);

  // With a withholding rule the voucher settles more than the lines: the customer owes the gross,
  // and the difference is a credit this company can reclaim from the tax authority.
  const selectedWithholdingRule = withholdingRules.find(r => r.id === withholdingRuleId);
  const withheldAmount = selectedWithholdingRule
    ? withheldOnNet(totalAmount, selectedWithholdingRule.withholdingPercent) : 0;
  const grossAmount = totalAmount + withheldAmount;
  // Mirrors the server's own check (WithholdingCalculator.CalculateAsync): below the rule's
  // threshold, nothing is actually withheld and the rule is refused at save. The figures above
  // still show the arithmetic — this only warns that it won't take effect, so the preview can't
  // show a confident number for something that's about to be rejected.
  const belowWithholdingThreshold =
    !!selectedWithholdingRule && grossAmount * exchangeRate < selectedWithholdingRule.thresholdAmount;

  // The receipt's headline method, read from its lines rather than asked for again (ML-01).
  const headerPaymentMethod = deriveVoucherPaymentMethod<PaymentMethod>(lines, 'Cash');

  // ── Invoices this receipt settles ─────────────────────────────────────────
  // Applied server-side at posting, in the same transaction that creates the credit.
  const [allocations, setAllocations] = useState<VoucherAllocationPick[]>([]);
  // The allocations as saved, with their document numbers — what a read-only voucher shows (ADR-003).
  const [savedAllocations, setSavedAllocations] = useState<SavedVoucherAllocation[] | undefined>(undefined);
  // Reported by the picker, which is the only place that knows which picks are advances. Summing
  // every allocation against the tender was right until an advance could fund part of the
  // settlement, and then it refused exactly the vouchers ML-07 exists to allow.
  const [settlement, setSettlement] = useState<VoucherAllocationSummary>(
    { settling: 0, advanceApplied: 0, overApplied: false, advanceStranded: false });

  const overAllocated = settlement.overApplied || settlement.advanceStranded;

  // ── Receipt state ──────────────────────────────────────────────────────────
  const [receipt,  setReceipt]  = useState<ArReceiptDto | null>(null);
  /** This receipt's cheques still in hand — the Deposit action takes them to a new bank deposit (424365). */
  const [chequesInHand, setChequesInHand] = useState(0);
  useEffect(() => {
    if (!receipt?.receiptNumber || receipt.workflowStatus !== 'Posted') { setChequesInHand(0); return; }
    chequeApi.inHandForReference(receipt.receiptNumber)
      .then(cs => setChequesInHand(cs.length)).catch(() => setChequesInHand(0));
  }, [receipt?.receiptNumber, receipt?.workflowStatus]);
  const [loading,  setLoading]  = useState(!isNew);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState<string | null>(null);
  const [success,  setSuccess]  = useState<string | null>(null);
  const [tab,      setTab]      = useState<Tab>('lines');
  const [modal,    setModal]    = useState<'approve' | 'reject' | 'reverse' | 'cancel' | 'delete' | null>(null);
  const [printOpen, setPrintOpen] = useState(false);

  // ── Load receipt ──────────────────────────────────────────────────────────
  const loadReceipt = useCallback(async () => {
    if (isNew || !id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await arReceiptApi.getById(id);
      const rct = res.data?.data;
      if (!rct) throw new Error('Receipt not found');
      setReceipt(rct);

      setCustomerId(rct.customerId);
      setCustomerCode(rct.customerCode);
      setCustomerName(rct.customerName);
      setControlAccountId(rct.controlAccountId ?? null);
      setReceiptDate(rct.receiptDate.split('T')[0]);
      setPostingDate(rct.postingDate.split('T')[0]);
      setCurrencyCode(rct.currencyCode);
      setExchangeRate(rct.exchangeRate);
      setReference(rct.reference ?? '');
      setNarration(rct.narration ?? '');
      setWithholdingRuleId(rct.withholdingTaxRuleId ?? '');
      setSavedWithholdingCode(rct.withholdingRuleCode ?? null);
      setLocationId(rct.locationId ?? '');
      setCostCenterId(rct.costCenterId ?? '');

      setSavedAllocations(rct.allocations ?? []);
      setAllocations((rct.allocations ?? []).map(a => ({
        openItemId:      a.openItemId,
        allocatedAmount: a.allocatedAmount,
      })));

      setLines(rct.lines.map(l => ({
        key:           l.id,
        lineNumber:    l.lineNumber,
        paymentType:   l.paymentType as PaymentMethod,
        // Receipts saved before ML-01 could leave a line blank and inherit the header's account.
        // The header no longer carries one, so it moves onto the line here.
        bankAccountId: l.bankAccountId ?? rct.bankAccountId ?? '',
        fundingGlAccountId: l.fundingGlAccountId ?? '',
        chequeNumber:  l.chequeNumber ?? '',
        chequeDate:    l.chequeDate ? l.chequeDate.split('T')[0] : '',
        drawerBankId:   l.drawerBankId ?? '',
        drawerBranchId: l.drawerBranchId ?? '',
        cardReference: l.cardReference ?? '',
        amount:        l.amount,
        currencyCode:  l.currencyCode,
        exchangeRate:  l.exchangeRate,
        remarks:       l.remarks ?? '',
      })));

      dispatch(setErpContext({
        companyCode:         authUser?.companyId?.substring(0, 8).toUpperCase() ?? 'COMPANY',
        companyName:         '',
        locationCode:        null,
        locationName:        null,
        costCenterCode:      null,
        costCenterName:      null,
        financialYearCode:   rct.financialYearCode,
        financialPeriodCode: rct.financialPeriodCode,
        transactionTypeCode: 'RCT',
        documentNumber:      rct.receiptNumber,
        currencyCode:        rct.currencyCode,
        exchangeRate:        rct.exchangeRate,
      }));
    } catch (err: unknown) {
      setError((err as Error).message ?? 'Failed to load receipt');
    } finally {
      setLoading(false);
    }
  }, [id, isNew, dispatch, authUser]);

  useEffect(() => { loadReceipt(); }, [loadReceipt]);
  useEffect(() => { return () => { dispatch(clearErpContext()); }; }, [dispatch]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const status = receipt?.workflowStatus ?? 'Draft';
  const isDraftEditable = isNew || status === 'Draft' || status === 'Rejected';
  const isEditable      = isDraftEditable && (isNew ? hasPermission(Permissions.AR_RECEIPT_CREATE) : canEdit);

  // ── Build payload ─────────────────────────────────────────────────────────
  const buildAllocations = (): ArReceiptAllocationRequest[] =>
    allocations
      .filter(a => a.allocatedAmount > 0)
      .map(a => ({ openItemId: a.openItemId, allocatedAmount: a.allocatedAmount, remarks: null }));

  const buildLines = (): ArReceiptLineRequest[] =>
    lines.filter(l => l.amount > 0).map((l, idx) => ({
      lineNumber:     idx + 1,
      paymentType:    l.paymentType,
      bankAccountId:  l.bankAccountId || null,
      fundingGlAccountId: l.fundingGlAccountId || null,
      chequeNumber:   l.chequeNumber || null,
      chequeDate:     l.chequeDate || null,
      drawerBankId:   l.drawerBankId || null,
      drawerBranchId: l.drawerBranchId || null,
      cardReference:  l.cardReference || null,
      amount:         l.amount,
      currencyCode:   l.currencyCode || currencyCode,
      exchangeRate:   l.exchangeRate || exchangeRate,
      remarks:        l.remarks || null,
    }));

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setError(null);
    if (!customerId) { setError('Please select a customer.'); return; }
    const linesPayload = buildLines();
    if (linesPayload.length === 0) { setError('At least one payment line with amount > 0 is required.'); return; }
    if (totalAmount <= 0) { setError('Receipt total must be greater than zero.'); return; }
    if (overAllocated) {
      setError(settlement.advanceStranded
        ? 'This receipt applies more advance than it settles. Settle more, or apply less of it.'
        : 'This receipt settles more than it collects and its advances cover. '
        + 'Reduce what it settles, increase the amount collected, or apply an advance.');
      return;
    }

    setSaving(true);
    try {
      if (isNew) {
        const selectedLoc = accessibleLocations.find(l => l.id === locationId);
        const payload: CreateArReceiptRequest = {
          locationId:   locationId   || null,
          locationCode: selectedLoc?.code ?? null,
          costCenterId: costCenterId || null,
          customerId, customerCode, customerName,
          controlAccountId: controlAccountId ?? null,
          receiptDate, postingDate,
          paymentMethod: headerPaymentMethod,
          // Each line names the account the money lands in (ML-01).
          bankAccountId: null,
          currencyCode, exchangeRate,
          reference: reference || null,
          narration: narration || null,
          withholdingTaxRuleId: withholdingRuleId || null,
          lines: linesPayload,
          allocations: buildAllocations(),
        };
        const res = await arReceiptApi.create(payload);
        const newId = res.data?.data?.id;
        setSuccess('Receipt created successfully.');
        setTimeout(() => navigate(`/ar/receipts/${newId}`), 800);
      } else if (receipt) {
        const payload: UpdateArReceiptRequest = {
          locationId:   locationId   || null,
          costCenterId: costCenterId || null,
          customerId, customerCode, customerName,
          controlAccountId: controlAccountId ?? null,
          receiptDate, postingDate,
          paymentMethod: headerPaymentMethod,
          // Each line names the account the money lands in (ML-01).
          bankAccountId: null,
          currencyCode, exchangeRate,
          reference: reference || null,
          narration: narration || null,
          withholdingTaxRuleId: withholdingRuleId || null,
          lines: linesPayload,
          allocations: buildAllocations(),
        };
        await arReceiptApi.update(receipt.id, payload);
        setSuccess('Receipt updated.');
        setTimeout(() => { setSuccess(null); loadReceipt(); }, 800);
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to save receipt.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  // ── Workflow actions ──────────────────────────────────────────────────────
  const doAction = async (action: () => Promise<void>, successMsg: string) => {
    setError(null);
    setSaving(true);
    try {
      await action();
      setSuccess(successMsg);
      setTimeout(() => { setSuccess(null); loadReceipt(); }, 1200);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Action failed.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit  = () => doAction(async () => { await arReceiptApi.submit(receipt!.id); }, 'Receipt submitted for approval.');
  const handlePost    = () => doAction(async () => { await arReceiptApi.post(receipt!.id); }, 'Receipt posted to general ledger.');
  const handleApprove = (remarks: string) => doAction(async () => { await arReceiptApi.approve(receipt!.id, remarks); }, 'Receipt approved.');
  const handleReject  = (reason: string)  => doAction(async () => { await arReceiptApi.reject(receipt!.id, reason); }, 'Receipt rejected.');
  const handleCancel  = (reason: string)  => doAction(async () => { await arReceiptApi.cancel(receipt!.id, reason); }, 'Receipt cancelled.');

  const handleDelete = async () => {
    if (!receipt) return;
    setSaving(true); setError(null);
    try {
      await arReceiptApi.delete(receipt.id);
      navigate(AppRoutes.AR_RECEIPTS, { replace: true });
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e?.message ?? 'Delete failed.');
    } finally { setSaving(false); }
  };
  const handleReverse = (narr: string)    => doAction(async () => {
    const today2 = new Date().toISOString().split('T')[0];
    const res = await arReceiptApi.reverse(receipt!.id, today2, today2, narr || undefined);
    const revId = res.data?.data?.id;
    setTimeout(() => navigate(`/ar/receipts/${revId}`), 1200);
  }, 'Reversal receipt created.');

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return <div className="flex items-center justify-center h-full"><Spinner size="lg" /></div>;
  }

  // ── Render ────────────────────────────────────────────────────────────────
  const lineColumns: LineColumn<LineRow>[] = [
    {
      key: 'payment-type', header: 'Payment Type', width: 'w-36',
      render: (line, idx) => (
        <>
          {isEditable ? (
            <Select value={line.paymentType} size="xs"
              aria-label={`Payment type line ${idx + 1}`}
              onChange={e => {
                const method = e.target.value as PaymentMethod;
                // Blank what the old method owned — including the drawer's bank and
                // branch, which belong to a cheque someone handed us and to nothing
                // else. Left behind they are invisible and still reach the server.
                updateLine(line.key, {
                  ...clearedByMethodChange(method),
                  paymentType: method,
                  // Mandatory where the company configures it (QC Part III item 3),
                  // so it opens on the receipt date rather than blank. The cheque in
                  // front of the cashier usually carries today; a post-dated one is
                  // changed from here.
                  ...(method === 'Cheque' && !line.chequeDate
                    ? { chequeDate: receiptDate }
                    : {}),
                });

                // Choosing Cheque is the moment the drawer's bank matters, so the
                // detail opens with it rather than behind a second click.
                if (method === 'Cheque')
                  setOpenDetail(open => new Set(open).add(line.key));
              }}
              // A saved line may name a method the company has since stopped allowing.
              // Keep it so an existing receipt still shows what it says.
              options={(allowedMethods.includes(line.paymentType)
                  ? allowedMethods
                  : [...allowedMethods, line.paymentType]
                ).map(m => ({ value: m, label: PAYMENT_METHOD_LABELS[m] }))}
            />
          ) : (
            <span className="text-sm text-slate-700 dark:text-slate-300">
              {PAYMENT_METHOD_LABELS[line.paymentType]}
            </span>
          )}
        </>
      ),
    },
    {
      key: 'receive-into', header: 'Receive Into', width: 'w-64',
      render: (line) => (
        <>
          <FundingSourceSelect
            paymentMethod={line.paymentType}
            forAp={false}
            disabled={!isEditable}
            locationId={locationId || null}
            currencyCode={line.currencyCode || null}
            value={{
              bankAccountId:      line.bankAccountId || null,
              fundingGlAccountId: line.fundingGlAccountId || null,
            }}
            onChange={v => updateLine(line.key, {
              bankAccountId:      v.bankAccountId ?? '',
              fundingGlAccountId: v.fundingGlAccountId ?? '',
            })}
          />
        </>
      ),
    },
    {
      key: 'cheque-no', header: 'Cheque No.', width: 'w-32',
      render: (line, idx) => (
        <>
          <Input size="xs" value={line.chequeNumber}
            aria-label={`Cheque number line ${idx + 1}`}
            onChange={e => updateLine(line.key, { chequeNumber: e.target.value })}
            disabled={!isEditable || (line.paymentType !== 'Cheque')}
            placeholder={line.paymentType === 'Cheque' ? 'Cheque no…' : '—'}
          />
        </>
      ),
    },
    {
      key: 'cheque-date', header: 'Cheque Date', width: 'w-32',
      render: (line, idx) => (
        <>
          <Input size="xs" type="date" value={line.chequeDate}
            aria-label={`Cheque date line ${idx + 1}`}
            onChange={e => updateLine(line.key, { chequeDate: e.target.value })}
            disabled={!isEditable || (line.paymentType !== 'Cheque')}
          />
        </>
      ),
    },
    {
      key: 'card-ref-no', header: 'Card/Ref No.', width: 'w-32',
      render: (line, idx) => (
        <>
          <Input size="xs" value={line.cardReference}
            aria-label={`Card or reference line ${idx + 1}`}
            onChange={e => updateLine(line.key, { cardReference: e.target.value })}
            disabled={!isEditable || !['CreditCard', 'DebitCard', 'BankTransfer', 'OnlineBanking'].includes(line.paymentType)}
            placeholder={['CreditCard', 'DebitCard', 'BankTransfer', 'OnlineBanking'].includes(line.paymentType) ? 'Reference…' : '—'}
          />
        </>
      ),
    },
    {
      key: 'amount', header: 'Amount', align: 'right', width: 'w-32',
      render: (line, idx) => (
        <>
          <Input size="xs" type="number" value={line.amount || ''}
            aria-label={`Amount line ${idx + 1}`}
            onChange={e => updateLine(line.key, { amount: parseFloat(e.target.value) || 0 })}
            disabled={!isEditable} min="0" step="0.01"
            className="text-right font-mono font-semibold"
          />
        </>
      ),
    },
    {
      key: 'ccy', header: 'CCY', width: 'w-20', className: 'text-xs font-mono text-slate-500 dark:text-gray-400',
      render: (line, idx) => (
        <>
          {currencies.length > 0 && isEditable ? (
            <Select size="xs" value={line.currencyCode || currencyCode}
              aria-label={`Currency line ${idx + 1}`}
              onChange={e => updateLine(line.key, { currencyCode: e.target.value })}
              className="font-mono"
              options={currencies.map(c => ({ value: c.code, label: c.code }))}
            />
          ) : (
            <span>{line.currencyCode || currencyCode}</span>
          )}
        </>
      ),
    },
    {
      key: 'remarks', header: 'Remarks', width: 'min-w-[140px]',
      render: (line, idx) => (
        <>
          <Input size="xs" value={line.remarks}
            aria-label={`Remarks line ${idx + 1}`}
            onChange={e => updateLine(line.key, { remarks: e.target.value })}
            disabled={!isEditable} placeholder="Note…"
          />
        </>
      ),
    },
  ];

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900 overflow-hidden">

      {/* ERP Context Bar */}
      <ERPContextBar />

      <MasterToolbar
        onRefresh={() => void loadReceipt()}
        refreshEnabled={!isNew}
        onSave={() => void handleSave()}
        saveEnabled={isEditable}
        onEdit={() => {}}
        onPrint={() => setPrintOpen(true)}
        printEnabled={!isNew && !!receipt && canPrint}
        onDelete={() => setModal('delete')}
        deleteEnabled={!isNew && status === 'Draft' && canDelete}
        onExit={() => navigate(AppRoutes.AR_RECEIPTS)}
        saving={saving}
        actions={[
          ...(!isNew && status === 'Draft' && canSubmit
            ? [{ key: 'submit', label: 'Submit', icon: <Send size={15} />, color: 'sky' as const, onClick: () => void handleSubmit() }] : []),
          ...(!isNew && (status === 'Submitted' || status === 'PendingApproval') && canApprove
            ? [{ key: 'approve', label: 'Approve', icon: <CheckCircle2 size={15} />, color: 'emerald' as const, onClick: () => setModal('approve') }] : []),
          ...(!isNew && (status === 'Submitted' || status === 'PendingApproval') && canReject
            ? [{ key: 'reject', label: 'Reject', icon: <XCircle size={15} />, color: 'rose' as const, onClick: () => setModal('reject') }] : []),
          ...(!isNew && status === 'Approved' && canPost
            ? [{ key: 'post', label: 'Post', icon: <BookCheck size={15} />, color: 'teal' as const, onClick: () => void handlePost() }] : []),
          ...(!isNew && status === 'Posted' && canReverse && !receipt?.reversalReceiptId
            ? [{ key: 'reverse', label: 'Reverse', icon: <RotateCcw size={15} />, color: 'amber' as const, onClick: () => setModal('reverse') }] : []),
          ...(!isNew && !['Posted', 'Cancelled', 'Reversed'].includes(status) && canCancel
            ? [{ key: 'cancel', label: 'Cancel', icon: <Ban size={15} />, color: 'rose' as const, onClick: () => setModal('cancel') }] : []),
        ]}
      />

      <MasterFlash message={error ? { type: 'error', text: error } : success ? { type: 'success', text: success } : null} />

      <PageHeader
        icon={Banknote}
        title={isNew ? 'New Customer Receipt' : `Receipt ${receipt?.receiptNumber ?? ''}`}
        subtitle={!isNew && receipt
          ? `Created ${new Date(receipt.createdDate).toLocaleString()}${receipt.modifiedDate ? ` · Modified ${new Date(receipt.modifiedDate).toLocaleString()}` : ''}`
          : undefined}
        actions={!isNew ? (
          <div className="flex items-center gap-2">
            {receipt?.originalReceiptId && (
              <Button variant="ghost" size="xs" rightIcon={<ExternalLink size={10} />}
                onClick={() => navigate(`/ar/receipts/${receipt.originalReceiptId}`)}>View Original</Button>
            )}
            {receipt?.reversalReceiptId && (
              <Button variant="ghost" size="xs" rightIcon={<ExternalLink size={10} />}
                onClick={() => navigate(`/ar/receipts/${receipt.reversalReceiptId}`)}>View Reversal</Button>
            )}
            {chequesInHand > 0 && hasPermission(Permissions.BNK_DEPOSIT_CREATE) && receipt && (
              <Button variant="outline" size="xs" leftIcon={<Banknote size={12} />}
                title="Open a new bank deposit with this receipt's cheques"
                onClick={() => navigate(`${AppRoutes.BANK_DEPOSIT_NEW}?receipt=${encodeURIComponent(receipt.receiptNumber)}`)}>
                Deposit {chequesInHand === 1 ? 'cheque' : `${chequesInHand} cheques`}
              </Button>
            )}
            {receipt?.sourceGlJournalId && (
              <Button variant="ghost" size="xs" rightIcon={<ExternalLink size={10} />}
                onClick={() => navigate(`/gl/journals/${receipt.sourceGlJournalId}`)}>View GL Journal</Button>
            )}
            {receipt?.isReversal && (
              <span className="px-2 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 text-xs font-semibold rounded-full">
                Reversal
              </span>
            )}
            <span className={cn('px-3 py-1 rounded-full text-xs font-semibold', STATUS_COLORS[status] ?? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-gray-400')}>
              {status}
            </span>
          </div>
        ) : undefined}
      />

      {/* Header fields */}
      <div className="flex-shrink-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">

          {/* Customer — found through the search modal (server-side search), shown as code — name */}
          <Field label="Customer *" className="col-span-2">
            <div className="flex items-center gap-1.5">
              <Input
                readOnly
                aria-label="Customer"
                value={customerId ? `${customerCode} — ${customerName}` : ''}
                placeholder={isEditable ? 'Select customer…' : '—'}
                onClick={() => isEditable && setCustOpen(true)}
                fieldClassName="flex-1"
                className={cn(isEditable && 'cursor-pointer')}
              />
              {isEditable && (
                <IconButton label="Search customers" icon={<Search size={14} />} onClick={() => setCustOpen(true)} />
              )}
              {isEditable && customerId && (
                <IconButton label="Clear customer" icon={<X size={14} />}
                  onClick={() => { setCustomerId(''); setCustomerCode(''); setCustomerName(''); setControlAccountId(null); }} />
              )}
            </div>
          </Field>

          <Input label="Receipt Date *" type="date" value={receiptDate}
            onChange={e => setReceiptDate(e.target.value)} disabled={!isEditable} />

          <Input label="Posting Date *" type="date" value={postingDate}
            onChange={e => setPostingDate(e.target.value)} disabled={!isEditable} />

          {currencies.length > 0 ? (
            <Select label="Currency *" value={currencyCode} onChange={e => changeCurrency(e.target.value)}
              disabled={!isEditable} className="font-mono"
              options={currencies.map(c => ({ value: c.code, label: `${c.code} — ${c.name}` }))} />
          ) : (
            <Input label="Currency *" value={currencyCode} onChange={e => changeCurrency(e.target.value.toUpperCase())}
              disabled={!isEditable} maxLength={10} className="font-mono uppercase" />
          )}

          <Input label="Exchange Rate" type="number" value={exchangeRate}
            onChange={e => setExchangeRate(parseFloat(e.target.value) || 1)}
            disabled={!isEditable} step="0.000001" min="0.000001" />

          <Input label="Reference" value={reference} onChange={e => setReference(e.target.value)}
            disabled={!isEditable} placeholder="Optional" />

          <Select label="Location" value={locationId} onChange={e => setLocationId(e.target.value)}
            disabled={!isEditable} fieldClassName="col-span-2 md:col-span-3"
            placeholder="— All Locations —"
            options={accessibleLocations.map(l => ({ value: l.id, label: `${l.code} — ${l.name}` }))} />

          <Select label="Cost Center" value={costCenterId} onChange={e => setCostCenterId(e.target.value)}
            disabled={!isEditable} fieldClassName="col-span-2 md:col-span-3"
            placeholder="— None —"
            options={costCentresForLocation(accessibleCostCenters, locationId).map(cc => ({ value: cc.id, label: `${cc.code} — ${cc.name}` }))} />

          <Input label="Narration" value={narration} onChange={e => setNarration(e.target.value)}
            disabled={!isEditable} placeholder="Describe this receipt…"
            fieldClassName="col-span-2 md:col-span-3 lg:col-span-6" />
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        tabs={TABS.map(t => t.label)}
        active={TABS.find(t => t.key === tab)?.label ?? TABS[0].label}
        onChange={label => setTab(TABS.find(t => t.label === label)?.key ?? 'lines')}
        className="static"
      />

      {/* Tab body */}
      <div className="flex-1 overflow-auto">

        {/* ── PAYMENT LINES ─────────────────────────────────────────────────── */}
        {tab === 'settles' && (
          <div className="p-6">
            <VoucherAllocationPicker
              canRaiseOnAccount={canRaiseOnAccount}
              onSummaryChange={setSettlement}
              partyType="Customer"
              partyId={customerId}
              currencyCode={currencyCode}
              tenderTotal={totalAmount}
              value={allocations}
              onChange={setAllocations}
              readOnly={!isEditable}
              saved={savedAllocations}
            />
          </div>
        )}

        {tab === 'lines' && (
          <LinesGrid
            rows={lines}
            columns={lineColumns}
            rowKey={line => line.key}
            lead={(line, idx) => (
              line.paymentType === 'Cheque' ? (
                <IconButton
                  variant="bare"
                  size="xs"
                  onClick={() => toggleDetail(line.key)}
                  label={`Cheque details line ${idx + 1}`}
                  aria-expanded={openDetail.has(line.key)}
                  title={line.drawerBankId ? 'Drawer bank recorded' : 'Cheque details'}
                  className={cn(line.drawerBankId && 'text-brand-500 hover:text-brand-600')}
                  icon={
                    <ChevronRight
                      size={12}
                      className={cn('transition-transform',
                        openDetail.has(line.key) && 'rotate-90')}
                    />
                  }
                />
              ) : <span className="w-[17px]" />
            )}
            detail={(line, idx) => (line.paymentType === 'Cheque' && openDetail.has(line.key)) ? (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                <span className="text-slate-400 dark:text-slate-500">Drawn on</span>

                <Select size="xs" value={line.drawerBankId}
                  onChange={e => updateLine(line.key,
                    { drawerBankId: e.target.value, drawerBranchId: '' })}
                  disabled={!isEditable}
                  aria-label={`Drawer bank line ${idx + 1}`}
                  fieldClassName="w-48"
                  placeholder="Bank…"
                  options={drawerBanks.map(b => ({ value: b.id, label: b.bankName }))}
                />

                <Select size="xs" value={line.drawerBranchId}
                  onChange={e => updateLine(line.key, { drawerBranchId: e.target.value })}
                  disabled={!isEditable || !line.drawerBankId}
                  aria-label={`Drawer branch line ${idx + 1}`}
                  fieldClassName="w-48"
                  placeholder={line.drawerBankId ? 'Branch…' : 'Pick a bank first'}
                  options={drawerBranches.filter(br => br.bankId === line.drawerBankId)
                    .map(br => ({ value: br.id, label: br.branchName }))}
                />
              </div>
            ) : null}
            onRemove={isEditable ? line => removeLine(line.key) : undefined}
            footer={
              <>
              {isEditable && (
                <div className="px-4 py-2 border-b border-slate-100 dark:border-slate-700">
                  <Button variant="ghost" size="sm" leftIcon={<Plus size={14} />} onClick={addLine}>
                    Add Payment Line
                  </Button>
                </div>
              )}

              {(withholdingRules.length > 0 || withholdingRuleId) && (
                <div className="flex items-center justify-end gap-3 px-6 py-2 border-b border-slate-100 dark:border-slate-700">
                  <span className="text-xs text-slate-500 dark:text-gray-400">Withholding tax</span>
                  <Select
                    id="ar-withholding-rule"
                    aria-label="Withholding tax"
                    value={withholdingRuleId}
                    onChange={e => setWithholdingRuleId(e.target.value)}
                    disabled={!isEditable}
                    placeholder="None"
                    fieldClassName="w-72"
                    options={[
                      ...(withholdingRuleId && !selectedWithholdingRule
                        ? [{ value: withholdingRuleId, label: savedWithholdingCode ?? 'Saved rule' }] : []),
                      ...withholdingRules.map(r => ({
                        value: r.id,
                        label: `${r.ruleCode} — ${r.withholdingPercent}% from ${fmt(r.thresholdAmount)}`,
                      })),
                    ]}
                  />
                </div>
              )}

              <div className="flex items-center justify-end gap-8 px-6 py-3 bg-slate-50 dark:bg-slate-800/80">
                {withheldAmount > 0 ? (
                  <>
                    <div className="text-right">
                      <p className="text-xs text-slate-500 dark:text-gray-400 mb-0.5">Received ({currencyCode})</p>
                      <p className="text-sm font-semibold font-mono text-slate-700 dark:text-slate-300">{fmt(totalAmount)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-500 dark:text-gray-400 mb-0.5">
                        Withheld{selectedWithholdingRule ? ` (${selectedWithholdingRule.withholdingPercent}%)` : ''}
                      </p>
                      <p className="text-sm font-semibold font-mono text-slate-700 dark:text-slate-300">{fmt(withheldAmount)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-500 dark:text-gray-400 mb-0.5">Settles ({currencyCode})</p>
                      <p className="text-base font-bold font-mono text-brand-700 dark:text-brand-400">{fmt(grossAmount)}</p>
                    </div>
                  </>
                ) : (
                  <div className="text-right">
                    <p className="text-xs text-slate-500 dark:text-gray-400 mb-0.5">Total ({currencyCode})</p>
                    <p className="text-base font-bold font-mono text-brand-700 dark:text-brand-400">{fmt(totalAmount)}</p>
                  </div>
                )}
                <div className="text-right">
                  <p className="text-xs text-slate-500 dark:text-gray-400 mb-0.5">Base Amount</p>
                  <p className="text-sm font-semibold font-mono text-slate-700 dark:text-slate-300">
                    {fmt(grossAmount * exchangeRate)}
                  </p>
                </div>
              </div>
              {belowWithholdingThreshold && selectedWithholdingRule && (
                <div className="flex items-start gap-2 px-6 py-2 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800/50 text-amber-800 dark:text-amber-300">
                  <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                  <p className="text-xs">
                    Below the {fmt(selectedWithholdingRule.thresholdAmount)} threshold for {selectedWithholdingRule.ruleCode} —
                    this rule won&rsquo;t actually apply when saved. Remove it, or raise the amount.
                  </p>
                </div>
              )}
              </>
            }
          />
        )}

        {/* ── WORKFLOW ──────────────────────────────────────────────────────── */}
        {tab === 'workflow' && (
          <div className="p-6 space-y-6">
            {!isNew && receipt && (
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
                <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-4">Approval Status</h3>
                <WorkflowStatusPanel entityName="ArReceipt" entityId={receipt.id} />
              </div>
            )}
            {isNew && (
              <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                <History size={28} className="mb-2 opacity-30" />
                <p className="text-sm">Save the receipt first to track workflow.</p>
              </div>
            )}
          </div>
        )}

        {/* ── ATTACHMENTS ──────────────────────────────────────────────────── */}
        {tab === 'attachments' && (
          <div className="p-6">
            {!isNew && receipt ? (
              <AttachmentPanel entityName="ArReceipt" entityId={receipt.id} canAdd={canAddDoc} canDelete={canDelDoc} />
            ) : (
              <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                <Paperclip size={28} className="mb-2 opacity-30" />
                <p className="text-sm">Save the receipt first to add attachments.</p>
              </div>
            )}
          </div>
        )}

        {/* ── AUDIT LOG ─────────────────────────────────────────────────────── */}
        {tab === 'audit' && (
          <div className="p-6">
            {!isNew && receipt ? (
              <AuditViewer entityName="ArReceipt" entityId={receipt.id} />
            ) : (
              <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                <ShieldCheck size={28} className="mb-2 opacity-30" />
                <p className="text-sm">Save the receipt first to view audit history.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action Modals */}
      {modal === 'approve' && (
        <ReasonDialog title="Approve Receipt" placeholder="Approval remarks (optional)…" required={false}
          onConfirm={text => { setModal(null); handleApprove(text); }} onCancel={() => setModal(null)} />
      )}
      {modal === 'reject' && (
        <ReasonDialog title="Reject Receipt" placeholder="Rejection reason (required)…" required={true}
          onConfirm={text => { setModal(null); handleReject(text); }} onCancel={() => setModal(null)} />
      )}
      {modal === 'reverse' && (
        <ReasonDialog title="Reverse Receipt" placeholder="Reversal narration (optional)…" required={false}
          onConfirm={text => { setModal(null); handleReverse(text); }} onCancel={() => setModal(null)} />
      )}
      {modal === 'cancel' && (
        <ReasonDialog title="Cancel Receipt" placeholder="Cancellation reason (required)…" required={true}
          onConfirm={text => { setModal(null); handleCancel(text); }} onCancel={() => setModal(null)} />
      )}
      {modal === 'delete' && (
        <ReasonDialog title="Delete Draft Receipt"
          placeholder="This draft receipt will be permanently removed. Type a note or leave blank."
          required={false}
          onConfirm={() => { setModal(null); handleDelete(); }}
          onCancel={() => setModal(null)} />
      )}

      {/* Customer search modal */}
      <MasterSearchModal<CustomerListDto>
        isOpen={custOpen}
        onClose={() => setCustOpen(false)}
        title="Select Customer"
        columns={CUSTOMER_COLUMNS}
        fetchData={fetchCustomers}
        onSelect={item => {
          setCustomerId(item.id);
          setCustomerCode(item.code);
          setCustomerName(item.name);
          if ((item as CustomerListDto & { controlAccountId?: string }).controlAccountId) {
            setControlAccountId((item as CustomerListDto & { controlAccountId?: string }).controlAccountId!);
          }
          if (item.currencyCode) setCurrencyCode(item.currencyCode);
          setCustOpen(false);
        }}
      />


      {/* Print Preview */}
      {receipt && (
        <MasterReportPreview
          isOpen={printOpen}
          onClose={() => setPrintOpen(false)}
          title={`Receipt ${receipt.receiptNumber}`}
          fileBaseName={`AR-Receipt-${receipt.receiptNumber}`}
          onDownloadExcel={() => exportToExcel({
            sheetName: 'Receipt',
            title:    `AR Receipt — ${receipt.receiptNumber}`,
            subtitle: `Customer: ${receipt.customerName ?? ''}   Date: ${receipt.receiptDate?.slice(0, 10) ?? ''}   Currency: ${receipt.currencyCode ?? ''}`,
            columns: [
              { header: '#',            key: 'lineNumber',    width: 6,  align: 'center' },
              { header: 'Payment Type', key: 'paymentType',   width: 16 },
              { header: 'Cheque No.',   key: 'chequeNumber',  width: 14 },
              { header: 'Cheque Date',  key: 'chequeDate',    width: 14 },
              { header: 'Ref / Card',   key: 'cardReference', width: 16 },
              { header: 'CCY',          key: 'currencyCode',  width: 8 },
              { header: 'Amount',       key: 'amount',        width: 16, numFmt: '#,##0.00', align: 'right' },
              { header: 'Remarks',      key: 'remarks',       width: 24 },
            ],
            rows: (receipt.lines ?? []).map(l => ({
              lineNumber:    l.lineNumber,
              paymentType:   l.paymentType   ?? '',
              chequeNumber:  l.chequeNumber  ?? '',
              chequeDate:    l.chequeDate?.slice(0, 10) ?? '',
              cardReference: l.cardReference ?? '',
              currencyCode:  l.currencyCode  ?? '',
              amount:        l.amount        || '',
              remarks:       l.remarks       ?? '',
            })),
            totalsRow: { cardReference: 'TOTAL', amount: receipt.totalAmount ?? 0 },
          }, `AR-Receipt-${receipt.receiptNumber}`)}
          email={{ documentType: 'AR Receipt', documentNo: receipt.receiptNumber }}
        >
          <ArReceiptVoucher
            receipt={receipt}
            companyName={authUser?.companyId ? '' : 'myLedger ONE'}
            printedBy={authUser?.username}
          />
        </MasterReportPreview>
      )}
    </div>
  );
};
